import { useCallback, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { Grid2 as Grid, Stack } from '@mui/material';
import RenderModuleField from '../../../common/fields/render-fields';
import BaseDialog, { BaseDialogProps } from '../../../common/base-dialog';
import { addDoc, collection, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import firebaseApp from '../../../../firebase-app';
import { isAllFieldsValid } from '../../../../utils/team';
import { Article } from '../types';
import { getEditArticleSchema } from '../UI-schema';
import { getFaqArticlePath } from '../../../../utils/paths';
import { FS_PATH_FAQ_ARTICLES } from '../../../../constants';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onDone: () => void;
  article: Article;
}

function EditArticleModal({ open, onClose, onDone, article }: Props) {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const valuesFields = useRef<Record<string, any>>(article);
  const editedFields = useRef<Record<string, boolean>>({});

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      if (article.id) {
        await setDoc(doc(firebaseApp.db, getFaqArticlePath(article.id)), valuesFields.current, {
          merge: true,
        });
      } else {
        await addDoc(collection(firebaseApp.db, FS_PATH_FAQ_ARTICLES), valuesFields.current);
      }

      onDone();
      onClose?.();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.saveError, {
        variant: 'error',
      });
    }
    setSubmitLoading(false);
  }, [strings, article, enqueueSnackbar, onDone, onClose]);

  const uiScheme = useMemo(() => {
    return getEditArticleSchema(article);
  }, [article]);

  const onChange = (fieldKey: string, value: any) => {
    valuesFields.current[fieldKey] = value;
    setIsDirty(true);
  };

  const onValidation = async (
    fieldKey: string,
    value: string | number | boolean,
    validation: any
  ) => {
    const isCurrentFieldValid: boolean = await validation.isValid(value);
    editedFields.current[fieldKey] = isCurrentFieldValid;
    const isFormValid = isAllFieldsValid(editedFields.current);
    setSubmitDisabled(!isFormValid);
  };

  const onDelete = async () => {
    if (article.id) {
      await deleteDoc(doc(firebaseApp.db, getFaqArticlePath(article.id)));
      onDone();
      onClose?.();
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} title={strings.faq.editArticle} maxWidth="md">
      <DialogContent dividers>
        <Grid container spacing={4}>
          {uiScheme?.fields.map((field) => (
            <Grid size={12} key={field.id}>
              <RenderModuleField
                field={field}
                setChange={onChange}
                setValidation={onValidation}
                path="article"
                fullWidth
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2} sx={{ justifyContent: 'space-between', flex: 1 }}>
          <Stack>
            {article.id && (
              <Button color="error" onClick={onDelete}>
                {strings.actions.delete}
              </Button>
            )}
          </Stack>
          <Stack direction="row" gap={2}>
            <Button color="primary" onClick={onClose}>
              {strings.actions.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={submitDisabled || !isDirty}
              loading={submitLoading}
              onClick={onSubmit}
            >
              {strings.actions.save}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
}

export default EditArticleModal;
